import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Presenter from './Presenter';
import { IContainerProps, IUserBooking } from './types';
import { IAppState } from '../../state/types';
import selectors from '../../state/selectors';
import actions from '../../state/actions';
import { BOOKING_STATUS } from '../../utils/constants';
import { isBookingExpired } from '../../utils/helpers';

const API_URL = process.env.REACT_APP_PLATFORM_API_BASE_URL;

const UserProfileContainer = (props: IContainerProps) => {
    const [isLoading, setIsLoading] = useState(true);
    const [upcomingBookings, setUpcomingBookings] = useState<IUserBooking[]>([]);
    const [pastBookings, setPastBookings] = useState<IUserBooking[]>([]);
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [bookingToCancel, setBookingToCancel] = useState<IUserBooking | null>(null);
    const [isCancelling, setIsCancelling] = useState(false);

    const history = useHistory();
    const dispatch = useDispatch();
    const authUser = useSelector((state: IAppState) => selectors.auth.getAuthUser(state));
    const rehydrated = useSelector((state: IAppState) => selectors.auth.getRehydrated(state));

    // Auth guard - redirect to login if not authenticated
    useEffect(() => {
        if (rehydrated && !authUser) {
            history.push('/login', { from: '/profile' });
        }
    }, [rehydrated, authUser, history]);

    // Fetch user bookings
    useEffect(() => {
        const fetchBookings = async () => {
            if (!authUser?.email) return;

            try {
                setIsLoading(true);
                const encodedEmail = encodeURIComponent(authUser.email.toLowerCase());
                const response = await fetch(`${API_URL}/api/users/${encodedEmail}/bookings`);

                if (!response.ok) {
                    throw new Error('Failed to fetch bookings');
                }

                const data = await response.json();
                console.log('=== BOOKINGS DATA FROM API ===');
                console.log('Raw data:', data);
                const bookings: IUserBooking[] = data.bookings || [];
                console.log('Bookings array:', bookings);
                console.log('First booking ID:', bookings[0]?.id);

                // Separate upcoming and past bookings using time-based filtering
                // A booking is "upcoming" only if: status is PENDING/CONFIRMED AND time hasn't passed
                const upcoming = bookings.filter(b =>
                    [BOOKING_STATUS.PENDING, BOOKING_STATUS.CONFIRMED].includes(b.status) &&
                    !isBookingExpired(b)  // Must be in the future
                );
                // A booking is "past" if: status is COMPLETED/CANCELLED/NO_SHOW OR time has passed
                const past = bookings.filter(b =>
                    [BOOKING_STATUS.COMPLETED, BOOKING_STATUS.CANCELLED, BOOKING_STATUS.NO_SHOW].includes(b.status) ||
                    isBookingExpired(b)  // Time has passed (regardless of status)
                );

                setUpcomingBookings(upcoming);
                setPastBookings(past);
            } catch (error) {
                console.error('Error fetching user bookings:', error);
            } finally {
                setIsLoading(false);
            }
        };

        if (authUser?.email) {
            fetchBookings();
        }
    }, [authUser?.email]);

    const handleLogout = () => {
        if (authUser) {
            dispatch(actions.auth.logoutRequest(authUser));
        }
        history.push('/');
    };

    // Open confirmation modal
    const handleCancelBooking = (bookingId: string) => {
        console.log('=== handleCancelBooking CALLED ===');
        console.log('bookingId received:', bookingId);
        console.log('upcomingBookings:', upcomingBookings);
        
        const booking = upcomingBookings.find(b => b.id === bookingId);
        console.log('Found booking:', booking);
        
        if (booking) {
            setBookingToCancel(booking);
            setCancelModalOpen(true);
        } else {
            console.error('Booking not found with id:', bookingId);
        }
    };

    // Close modal
    const handleCloseCancelModal = () => {
        setCancelModalOpen(false);
        setBookingToCancel(null);
    };

    // Confirm cancellation
    const handleConfirmCancel = async () => {
        console.log('=== handleConfirmCancel CALLED ===');
        console.log('bookingToCancel:', bookingToCancel);
        console.log('authUser:', authUser);
        
        if (!bookingToCancel?.id || !authUser?.email) {
            console.error('Missing data:', { bookingId: bookingToCancel?.id, email: authUser?.email });
            alert('Error: Datos faltantes para cancelar');
            return;
        }

        const url = `${API_URL}/api/users/bookings/${bookingToCancel.id}/cancel`;
        console.log('Cancel booking URL:', url);
        console.log('Request body:', { userEmail: authUser.email.toLowerCase() });

        try {
            setIsCancelling(true);
            const response = await fetch(url, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ userEmail: authUser.email.toLowerCase() })
            });

            console.log('Response status:', response.status);
            const responseText = await response.text();
            console.log('Response body:', responseText);

            if (!response.ok) {
                let errorMsg = 'Error desconocido';
                try {
                    const errorData = JSON.parse(responseText);
                    errorMsg = errorData.error || errorData.message || errorMsg;
                } catch (e) {
                    errorMsg = responseText || errorMsg;
                }
                console.error('Cancel booking failed:', response.status, errorMsg);
                alert('Error al cancelar la reserva: ' + errorMsg);
                return;
            }

            // Move booking from upcoming to past with cancelled status
            const cancelledBooking = { ...bookingToCancel, status: BOOKING_STATUS.CANCELLED };
            setUpcomingBookings(prev => prev.filter(b => b.id !== bookingToCancel.id));
            setPastBookings(prev => [cancelledBooking, ...prev]);
            
            handleCloseCancelModal();
        } catch (error) {
            console.error('Error cancelling booking:', error);
            alert('Error al cancelar la reserva: ' + (error as Error).message);
        } finally {
            setIsCancelling(false);
        }
    };

    const handleBookAgain = (booking: IUserBooking) => {
        // Navigate to business page to book again
        if (booking.businessId) {
            history.push(`/b/${booking.businessId}`);
        }
    };

    // Show nothing while checking auth
    if (!rehydrated) {
        return null;
    }

    // Will redirect if not authenticated
    if (!authUser) {
        return null;
    }

    return (
        <Presenter
            {...props}
            authUser={authUser}
            isLoading={isLoading}
            upcomingBookings={upcomingBookings}
            pastBookings={pastBookings}
            onLogout={handleLogout}
            onCancelBooking={handleCancelBooking}
            onBookAgain={handleBookAgain}
            cancelModalOpen={cancelModalOpen}
            bookingToCancel={bookingToCancel}
            onCloseCancelModal={handleCloseCancelModal}
            onConfirmCancel={handleConfirmCancel}
            isCancelling={isCancelling}
        />
    );
};

export default UserProfileContainer;
